import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { EnterpriseSearchService } from '@sabstravtech/obtservices/angular';
import { NamedPhoneNumber } from '@sabstravtech/obtservices/base';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export type ChatPhoneAnswer =
  | { kind: 'saved'; description: string; countryCode: string; areaCode?: string; number: string }
  | { kind: 'manual'; countryCode: string; number: string };

/** Sentinel dropdown value for the explicit "None" option. */
const NONE_ID = '__none__';

/**
 * Renders the phone-number picker widget inside the chat widget: a dropdown of
 * the passenger's saved numbers (only shown when there are any) plus a "None"
 * option, and manual country-code + number entry fields shown whenever no
 * saved number is selected. Continue is disabled without a value only when the
 * field is required; Skip is hidden entirely when required.
 */
@Component({
  selector: 'app-chat-phone-input',
  standalone: false,
  templateUrl: './chat-phone-input.component.html',
  styleUrls: ['./chat-phone-input.component.scss']
})
export class ChatPhoneInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() passengerName = '';
  @Input() savedNumbers: NamedPhoneNumber[] = [];
  @Input() required?: boolean;
  @Output() answer = new EventEmitter<ChatPhoneAnswer>();
  @Output() skip = new EventEmitter<void>();

  readonly NONE_ID = NONE_ID;

  selectedId = '';
  countryCode: string | null = '44';
  number = '';
  /** Human-readable summary of what was submitted (via Continue or Skip),
   * shown in place of the controls once this widget instance is locked. */
  chosenSummary: string | null = null;

  constructor(public searchService: EnterpriseSearchService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset the picker whenever the passenger/savedNumbers change (new
    // question) — but NOT merely because `locked` toggled, which would wipe
    // out the selection just as we want to display it as read-only.
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    const defaultNumber =
      this.savedNumbers.find((number) => number.userPrimary) ?? this.savedNumbers[0];
    this.selectedId = defaultNumber ? String(defaultNumber.id) : '';
    this.countryCode = '44';
    this.number = '';
    this.chosenSummary = null;
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  get hasSavedNumbers(): boolean {
    return this.savedNumbers.length > 0;
  }

  get selectedSavedNumber(): NamedPhoneNumber | null {
    if (!this.selectedId || this.selectedId === NONE_ID) {
      return null;
    }
    // Native <select> option values are always strings, but saved.id may come
    // back from the backend as a number — compare as strings on both sides so
    // a valid selection is never silently treated as "nothing chosen".
    return this.savedNumbers.find((n) => String(n.id) === this.selectedId) ?? null;
  }

  get showManualEntry(): boolean {
    return !this.hasSavedNumbers || this.selectedId === NONE_ID;
  }

  get isValid(): boolean {
    if (this.selectedSavedNumber) {
      return true;
    }
    if (this.countryCode && this.number?.trim()) {
      return true;
    }
    return !this.isMandatory;
  }

  formatSavedNumber(saved: NamedPhoneNumber): string {
    const countryCode = saved.countryCode ? `+${saved.countryCode.replace(/^\+/, '')}` : '';
    const number = [countryCode, saved.areaCode, saved.number].filter(Boolean).join(' ');
    return saved.description ? `${saved.description} - ${number}` : number;
  }

  /** Angular templates can't call the global String() directly. */
  idToString(id: string | number): string {
    return String(id);
  }

  onDropdownChange(id: string): void {
    if (this.locked) {
      return;
    }
    this.selectedId = id;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const saved = this.selectedSavedNumber;
    if (saved) {
      // Saved numbers may be stored/displayed with formatting spaces (e.g. a
      // UK local part like "123 4567") — the tool requires digits only, so
      // strip non-digits here rather than relying on the model to sanitise.
      const digitsOnly = (v?: string | null) => (v ?? '').replace(/\D/g, '');
      this.chosenSummary = this.formatSavedNumber(saved);
      this.answer.emit({
        kind: 'saved',
        description: saved.description || this.formatSavedNumber(saved),
        countryCode: saved.countryCode ? `+${saved.countryCode.replace(/^\+/, '')}` : '',
        areaCode: digitsOnly(saved.areaCode) || undefined,
        number: digitsOnly(saved.number)
      });
      return;
    }
    const number = this.number?.trim().replace(/\D/g, '');
    if (this.countryCode && number) {
      const countryCode = `+${this.countryCode.replace(/^\+/, '')}`;
      this.chosenSummary = `${countryCode} ${number}`;
      this.answer.emit({ kind: 'manual', countryCode, number });
      return;
    }
    // Only reachable for an optional field with nothing entered.
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }

  onSkip(): void {
    if (this.locked) {
      return;
    }
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }
}
